import { useMemo } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Loader2,
  MapPin,
} from "lucide-react";
import { useBulkSession, useBulkObjects } from "../hooks/queries";
import {
  WIZARD_STEPS,
  sessionView,
  classLabel,
  objectSizeText,
  containerText,
  fileStatusText,
  needsUserClass,
  objectLabel,
  observedOnText,
  buildWaitText,
} from "../lib/bulkIngest";
import type { BulkObjectRow } from "../types/contract";

// What is NOT here yet: the decision controls (classify answers, overlap verdicts, a date per
// undated sample set).

function Stepper({ status }: { status: string | null | undefined }) {
  const v = sessionView(status);
  if (v.failed) return null;
  return (
    <ol className="flex flex-wrap items-center gap-1.5 text-[11px]">
      {WIZARD_STEPS.map((s, i) => {
        const done = i < v.index;
        const here = i === v.index;
        return (
          <li
            key={s.key}
            className={`flex items-center gap-1 rounded px-1.5 py-0.5 ${
              here
                ? "bg-accent text-canvas"
                : done
                  ? "text-muted"
                  : "text-faint"
            }`}
          >
            {done ? (
              <CheckCircle2 size={11} />
            ) : here && !v.waiting ? (
              <Loader2 size={11} className="animate-spin" />
            ) : null}
            {s.label}
          </li>
        );
      })}
    </ol>
  );
}

function ObjectRow({ o }: { o: BulkObjectRow }) {
  const size = objectSizeText(o);
  const where = containerText(o);
  const cls = o.effective_class;
  const ask = needsUserClass(o);
  const when = observedOnText(o);
  return (
    <li className="flex items-start gap-2 rounded px-1.5 py-1 text-[11px] hover:bg-hover">
      <MapPin size={11} className="mt-0.5 shrink-0 text-faint" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-1.5">
          <span className="truncate font-medium text-fg">{objectLabel(o)}</span>
          {size && (
            <span className="shrink-0 tabular-nums text-faint">{size}</span>
          )}
          {where && <span className="shrink-0 text-faint">{where}</span>}
          {when && (
            <span className="shrink-0 tabular-nums text-faint">{when}</span>
          )}
        </div>
        {/* the REASONS, in discovery order — this is what `class_confidence` must never be rendered as */}
        {o.class_signals?.length ? (
          <div className="mt-0.5 text-[10px] text-faint">
            {o.class_signals.join(" · ")}
          </div>
        ) : null}
      </div>
      <span
        className={`shrink-0 rounded px-1 py-0.5 text-[10px] ${
          ask ? "bg-amber-500/15 text-amber-500" : "bg-raised text-muted"
        }`}
      >
        {cls ? classLabel(cls) : "Unclassified"}
      </span>
    </li>
  );
}

/*
 *
 * A not-found then is the window before the session row is visible -- NOT "never started" — "it
 * says thi drop hasn't been started yet.
 */
export function BulkSessionView({
  sessionId,
  startedNow = false,
  fieldCount,
}: {
  sessionId: string | null;
  startedNow?: boolean;
  fieldCount?: number;
}) {
  const {
    data: session,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useBulkSession(sessionId);
  // Ask for geometry only when we draw it; a drop can hold thousands of objects and the geometry is the heavy part.
  const { data: objects } = useBulkObjects(
    sessionId,
    { limit: 200 },
    !!session,
  );

  const v = sessionView(session?.status);
  const failedFiles = useMemo(
    () => (session?.files ?? []).filter((f) => f.status === "failed"),
    [session],
  );
  // Honest pre-dedup field count = objects classified Boundary (compute: the post-dedup number can only be
  // lower, so this is phrased as an upper bound). Fields build IMMEDIATELY on creation, GEE-gated at 2
  // concurrent fleet-wide -- so the cost is hours-to-a-day and must be said here, neutrally, since nothing
  // fails and the Errors menu is rightly silent about it.
  const boundaryCount = useMemo(
    () =>
      (objects?.rows ?? []).filter((o) => o.effective_class === "Boundary")
        .length,
    [objects],
  );

  if (!sessionId) return null;
  if (isLoading) {
    return (
      <p className="flex items-center gap-1.5 py-4 text-[11px] text-muted">
        <Loader2 size={12} className="animate-spin" /> Loading this drop…
      </p>
    );
  }
  // A read failure is NOT an empty session: BE-Light 503s rather than serving precisely so we can't
  // show a false all-clear over a store we can't see. But it is also NOT a failure of the DROP —
  // who watched his jobs run normally on th dashboard while this sat red).
  if (isError) {
    return (
      <div className="flex flex-col items-start gap-1.5 py-4">
        <p className="text-[11px] text-red-400">
          Couldn’t read this drop’s status
          {error instanceof Error ? ` — ${error.message}` : ""}.
        </p>
        <p className="text-[11px] text-muted">
          This is the status read, not the drop itself — processing continues
          server-side and nothing has been discarded. You can follow the jobs on
          the field in the meantime.
        </p>
        <div className="flex items-center gap-2">
          <button
            className="rounded border border-line px-2 py-1 text-[11px] text-muted hover:bg-hover hover:text-fg disabled:opacity-50"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            {isFetching ? "Retrying…" : "Retry now"}
          </button>
          <span className="text-[10px] text-faint">
            Retrying automatically every few seconds.
          </span>
        </div>
      </div>
    );
  }
  // `null` = the session row isn't readable yet. We mint the id before compute's row exists, so this
  // window is normal -- but what it MEANS depends on who started the drop. If we just filed it, saying
  // "hasn't been started yet" contradicts the line directly above it ("1 of 1 field queued") and tells the
  // user their import didn't happen when it did. Only a session we did NOT start can honestly say that.
  if (!session) {
    if (startedNow) {
      return (
        <div className="flex flex-col gap-1.5 py-4">
          <p className="flex items-center gap-1.5 text-[11px] text-muted">
            <Loader2 size={12} className="animate-spin" /> Importing…
          </p>
          <p className="text-[11px] text-faint">
            {/*
 No step is named: the read hasn't landed, so we know it started and nothing finer. A
                fabricated position on the stepper would be detail we don't have.
*/}
            This usually takes {buildWaitText(fieldCount)}. You can close this —
            it keeps running, and the fields appear as they’re created.
          </p>
        </div>
      );
    }
    return (
      <p className="py-4 text-[11px] text-faint">
        This drop hasn’t been started yet.
      </p>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <Stepper status={session.status} />

      {v.failed && (
        <div className="flex items-start gap-2 rounded border border-red-500/40 bg-red-500/10 px-2 py-1.5 text-[11px]">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-red-400" />
          <span>{session.error || "This drop failed to process."}</span>
        </div>
      )}

      {/*
 "still in flight" comes from the nonterminal counters (BE-Light rev 00119, which replaced the
          retired orphan/conflict-pending pair). Rendered ONLY when the field is actually present and
          non-zero: a missing counter must show nothing, never "0 still processing", which would announce
          completion on the strength of a number we never received.
*/}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted">
        <span>
          {session.object_count} object{session.object_count === 1 ? "" : "s"}
          {typeof session.nonterminal_object_count === "number" &&
            session.nonterminal_object_count > 0 && (
              <span className="text-amber-500">
                {" "}
                · {session.nonterminal_object_count} still processing
              </span>
            )}
        </span>
        <span>
          {session.file_count} file{session.file_count === 1 ? "" : "s"}
          {session.failed_file_count > 0 && (
            <span className="text-red-400">
              {" "}
              · {session.failed_file_count} failed
            </span>
          )}
          {typeof session.nonterminal_file_count === "number" &&
            session.nonterminal_file_count > 0 && (
              <span className="text-amber-500">
                {" "}
                · {session.nonterminal_file_count} still processing
              </span>
            )}
        </span>
      </div>

      {/* an un-extractable file must be flagged BY NAME — never a silent drop from the count. */}
      {failedFiles.length > 0 && (
        <div className="rounded border border-line">
          <div className="border-b border-line px-2 py-1 text-[11px] font-medium">
            Couldn’t be read
          </div>
          <ul className="p-1">
            {failedFiles.map((f) => (
              <li
                key={f.file_id}
                className="flex items-start gap-1.5 px-1 py-0.5 text-[11px]"
              >
                <FileText size={11} className="mt-0.5 shrink-0 text-red-400" />
                <span className="min-w-0 flex-1">
                  <span className="font-medium text-fg">
                    {f.original_filename ?? "A file"}
                  </span>
                  <span className="text-faint"> — {fileStatusText(f)}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="border-t border-line px-2 py-1 text-[10px] text-faint">
            Everything else in this drop was processed normally.
          </p>
        </div>
      )}

      {boundaryCount > 0 && (
        <p className="rounded border border-line px-2 py-1.5 text-[10px] text-faint">
          Up to {boundaryCount} new field{boundaryCount === 1 ? "" : "s"} from
          this drop. Each starts preparing its soil data as soon as it’s created
          — {buildWaitText(boundaryCount)}. You can keep working; maps fill in
          as they’re ready.
        </p>
      )}

      {objects && objects.rows.length > 0 && (
        <div className="flex min-h-0 flex-1 flex-col rounded border border-line">
          <div className="flex items-baseline justify-between border-b border-line px-2 py-1">
            <span className="text-[11px] font-medium">What we found</span>
            {objects.total > objects.rows.length && (
              <span className="text-[10px] text-faint">
                showing {objects.rows.length} of{" "}
                {objects.total.toLocaleString()}
              </span>
            )}
          </div>
          <ul className="min-h-0 flex-1 overflow-auto p-1">
            {objects.rows.map((o) => (
              <ObjectRow key={o.object_id} o={o} />
            ))}
          </ul>
        </div>
      )}

      {v.waiting && (
        <p className="rounded border border-line px-2 py-1.5 text-[10px] text-faint">
          This drop is waiting on a decision. The controls for it aren’t wired
          yet — nothing has been imported or discarded, and it will still be
          here when they are.
        </p>
      )}
    </div>
  );
}
